import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Modal, Chip } from "./ui-bits";
import { SkuLinkRow, useGarmentOptions } from "./SkuMatcher";
import { CheckCircle2, AlertTriangle, Lock, PackageCheck } from "lucide-react";

const STATE = {
  ok: { tone: "green", label: "Disponibile" },
  partial: { tone: "gold", label: "Parziale" },
  missing: { tone: "red", label: "Da ordinare" },
};
const n = (v) => Number(v || 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });

export const MaterialRowsTable = ({ rows, testPrefix, showReserved = false }) => (
  <div className="overflow-x-auto">
    <table className="tbl w-full text-xs [&_td]:!px-2 [&_th]:!px-2">
      <thead><tr>
        <th className="text-left">Materiale</th><th className="text-left">Fornitore</th>
        <th className="text-right">Serve</th>
        {showReserved && <th className="text-right">Di cui riservato</th>}
        <th className="text-right">In magazzino</th>
        {!showReserved && <th className="text-right">Libero</th>}
        {showReserved && <th className="text-right">In arrivo</th>}
        <th className="text-right">Da ordinare</th><th>Stato</th>
      </tr></thead>
      <tbody>
        {rows.map(r => {
          const toOrder = r.to_order ?? r.missing;
          const st = r.missing > 0 && toOrder === 0 ? { tone: "blue", label: "In arrivo" } : STATE[r.state];
          return (
          <tr key={r.material_id} data-testid={`${testPrefix}-row-${r.material_id}`}>
            <td><div className="font-medium">{r.name}</div><div className="text-xs opacity-60">{[r.code, r.color].filter(Boolean).join(" · ")}</div></td>
            <td className="text-xs">{r.supplier ? <><div>{r.supplier.name}</div><div className="opacity-60">{r.supplier.email || r.supplier.phone || ""}</div></> : <span className="opacity-50">—</span>}</td>
            <td className="text-right">{n(r.needed)} {r.unit}</td>
            {showReserved && <td className="text-right">{n(r.reserved)} {r.unit}</td>}
            <td className="text-right">{n(r.stock)} {r.unit}</td>
            {!showReserved && <td className="text-right" title={`Riservato da altri ordini: ${n(r.reserved_other)}`}>{n(r.available)} {r.unit}</td>}
            {showReserved && <td className="text-right" data-testid={`${testPrefix}-incoming-${r.material_id}`}>{r.incoming > 0 ? `${n(r.incoming)} ${r.unit}` : "—"}</td>}
            <td className="text-right font-medium" data-testid={`${testPrefix}-missing-${r.material_id}`}>{toOrder > 0 ? `${n(toOrder)} ${r.unit}` : "—"}</td>
            <td className="whitespace-nowrap"><Chip tone={st.tone}>{st.label}</Chip></td>
          </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export const UnmatchedList = ({ items, testId }) => !items?.length ? null : (
  <div className="mt-6 p-4 border border-amber-300 bg-amber-50/60 text-sm" data-testid={testId}>
    <div className="font-medium mb-2 flex items-center gap-2"><AlertTriangle size={14}/> Articoli senza distinta materiali</div>
    <ul className="text-xs space-y-1">
      {items.map((u, i) => (
        <li key={i}>{u.order_number ? `${u.order_number} · ` : ""}{u.product_name || "—"} {u.sku ? `(SKU ${u.sku})` : ""} × {u.quantity} —{" "}
          {u.reason === "no_garment" ? "nessun capo con questo codice SKU" : "il capo non ha materiali inseriti"}</li>
      ))}
    </ul>
  </div>
);

export function OrderMaterialsModal({ orderId, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const garments = useGarmentOptions();
  const reload = () => api.get(`/orders/${orderId}/materials`).then(r => setData(r.data)).catch(() => setErr("Impossibile calcolare i materiali. Riprova."));
  useEffect(() => {
    if (!orderId) return;
    setData(null); setErr("");
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);
  const missingCount = data ? data.rows.filter(r => r.missing > 0).length : 0;
  useEffect(() => {
    if (!orderId) return;
    const h = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [orderId, onClose]);
  return (
    <Modal open={!!orderId} onClose={onClose} title={`Materiali ordine ${data?.order_number || ""}`} wide>
      {err ? <div className="text-sm text-red-600" data-testid="order-materials-error">{err}</div> :
       !data ? <div className="text-sm opacity-60" data-testid="order-materials-loading">Calcolo in corso…</div> : (
        <div data-testid="order-materials-modal">
          <div className="flex flex-wrap gap-3 mb-6 text-sm">
            {data.all_available
              ? <span className="inline-flex items-center gap-2 text-green-700" data-testid="order-materials-summary"><CheckCircle2 size={16}/> Tutti i materiali sono in magazzino</span>
              : <span className="inline-flex items-center gap-2 text-red-700" data-testid="order-materials-summary"><AlertTriangle size={16}/> {missingCount === 1 ? "Manca 1 materiale" : `Mancano ${missingCount} materiali`}</span>}
            {data.reserved && <span className="inline-flex items-center gap-2 opacity-70"><Lock size={14}/> Materiali riservati per questo ordine</span>}
            {data.consumed && <span className="inline-flex items-center gap-2 opacity-70"><PackageCheck size={14}/> Materiali già scaricati dal magazzino</span>}
            {!data.reserved && !data.consumed && <span className="opacity-60">Verranno riservati quando l'ordine è Confermato</span>}
          </div>
          {data.rows.length ? <MaterialRowsTable rows={data.rows} testPrefix="order-mat"/> :
            <div className="text-sm opacity-60">Nessun materiale calcolabile per questo ordine.</div>}
          <UnmatchedList items={data.unmatched.filter(u => u.reason !== "no_garment")} testId="order-materials-unmatched"/>
          {data.unmatched.some(u => u.reason === "no_garment" && u.sku) && (
            <div className="mt-6" data-testid="order-materials-sku-link">
              <div className="label-caps mb-2">Prodotti da abbinare a un capo</div>
              {data.unmatched.filter(u => u.reason === "no_garment" && u.sku).map(u =>
                <SkuLinkRow key={u.sku} item={u} garments={garments} onLinked={reload}/>)}
            </div>
          )}
          {data.unmatched.some(u => u.reason === "no_garment" && !u.sku) &&
            <UnmatchedList items={data.unmatched.filter(u => u.reason === "no_garment" && !u.sku)} testId="order-materials-unmatched-nosku"/>}
        </div>
      )}
    </Modal>
  );
}
